import React, { useCallback, useMemo, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import PropTypes from 'prop-types';
import { useField } from 'formik';

import { hooks } from '@febos-io/design';

import './FormikInputFileDropZone.scss';
import useTranslation from '../../hooks/useTranslation';
import FormikInputFileDropZoneThumb from './FormikInputFileDropZoneThumb';
import { useUploadFilesMultipartToS3Callback } from '../../hooks/useUploadFilesMultipartToS3Callback';

const thumbsContainer = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: 1,
};

export function FormikInputFileDropZone({
  id: defaultId,
  label,
  name,
  placeholder,
  multiple = false,
  accept,
  company = undefined,
  fileFolder = 'exam',
  group = 'exam',
}) {
  const { t, } = useTranslation();

  const [, { value, }, helper] = useField(name);
  const [files, setFiles] = useState(value || {});

  const updateFile = useCallback(async (file) => {
    setFiles((prevState) => ({
      ...prevState,
      [file.key]: {
        ...file,
        ...prevState[file.key],
        ...(file.fileKey) && {
          fileKey: file.fileKey,
        },
        meta: {
          ...prevState[file.key]?.meta,
          ...file.meta,
        },
      },
    }));
  }, []);

  const {
    callback: uploadFiles,
    removeFile,
    loading: isLoadingFiles,
  } = useUploadFilesMultipartToS3Callback(files, updateFile, fileFolder);

  const onRemoveFile = useCallback(
    async (file) => {
      removeFile(file);
      const newFiles = Object.values(files)
        .filter((item) => item.key !== file.key)
        .reduce((acc, item) => {
          acc[item.key] = item;
          return acc;
        }, {});
      setFiles(newFiles);
      helper.setValue(newFiles);
    },
    [files, helper, removeFile]
  );

  const { id, ariaDescribedby, } = hooks.useInput(defaultId, label);

  const acceptFiles = useMemo(()=> {
    if (typeof accept === 'object') {
      return accept;
    }
    if (typeof accept === 'string') {
      const mimetypes = accept.split(',');
      return mimetypes.reduce((acc, mimetype) => {
        acc[mimetype] = [];
        return acc;
      }, {});
    }
    return undefined;
  }, [accept]);

  const { getRootProps, getInputProps, } = useDropzone({
    multiple,
    accept: acceptFiles,
    disabled: isLoadingFiles,
    onDrop: async (result) => {
      const pendingFiles = result.map((file) => {
        const date = new Date();
        const type = file.type || 'application/octet-stream';
        const key = `${type}/${date.getTime()}.${file.name}`
          .replaceAll(' ', '_')
          .replaceAll('Ñ', 'N')
          .replaceAll('ñ', 'n')
          .replaceAll(/[^A-Za-z0-9-_.!~*'()]/g, '_');
        return {
          preview: type.indexOf('image') >= 0 ? URL.createObjectURL(file) : null,
          company,
          date,
          name: file.name,
          size: file.size,
          type,
          file,
          key,
          group,
          fileKey: key,
          meta: {
            progress: 1,
          },
        };
      });
      const newFiles = pendingFiles.reduce((acc, file) => {
        acc[file.key] = file;
        return acc;
      }, files);
      setFiles(newFiles);
      helper.setValue(newFiles);
      await uploadFiles({ files: pendingFiles, });
      helper.setValue({
        ...value,
        ...files,
      });
    },
  });

  const thumbs = useMemo(() => {
    return Object.values(files).map((file) => (
      <FormikInputFileDropZoneThumb key={file.key} file={file} onRemove={onRemoveFile} />
    ));
  }, [files, onRemoveFile]);

  return (
    <section className="container">
      <div {...getRootProps({ className: 'dropzone', })}>
        <input
          id={id}
          placeholder={placeholder}
          {...(ariaDescribedby && {
            'aria-describedby': ariaDescribedby,
          })}
          {...getInputProps()}
          {...(accept && {
            accept,
          })}
        />
        <div className="d-flex align-items-center justify-content-center flex-column">
          <h5>{t('Drop Files here or click to upload')}</h5>
          <p className="text-secondary">
            {t('Drop files here or click to')}
            {' '}
            <b>{t('browse')}</b>
            {' '}
            {t('thorough your machine')}
          </p>
        </div>
      </div>
      <aside style={thumbsContainer}>{thumbs}</aside>
    </section>
  );
}

FormikInputFileDropZone.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  classInput: PropTypes.string,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  accept: PropTypes.string,
  company: PropTypes.string,
  fileFolder: PropTypes.string,
  group: PropTypes.string,
};

export default FormikInputFileDropZone;
